<template>
  <div class="lp-wrap">
    <div class="lp-card">

      <!-- Double-ring spinner with step icon centered inside -->
      <div class="lp-spinner-wrap">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"
             class="lp-svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <circle stroke-linecap="round" fill="none"
                  stroke-dasharray="50.26548245743669 50.26548245743669"
                  stroke="#d50032" stroke-width="8" r="32" cy="50" cx="50">
            <animateTransform values="0 50 50;360 50 50" keyTimes="0;1"
                              repeatCount="indefinite" dur="1s" type="rotate"
                              attributeName="transform" />
          </circle>
          <circle stroke-linecap="round" fill="none"
                  stroke-dashoffset="36.12831551628262"
                  stroke-dasharray="36.12831551628262 36.12831551628262"
                  stroke="#001e62" stroke-width="8" r="23" cy="50" cx="50">
            <animateTransform values="0 50 50;-360 50 50" keyTimes="0;1"
                              repeatCount="indefinite" dur="1s" type="rotate"
                              attributeName="transform" />
          </circle>
        </svg>

        <Transition name="lp-icon" mode="out-in">
          <i :key="currentStep"
             :class="['fa-solid', stepConfig[currentStep]?.icon ?? 'fa-circle-notch']"
             class="lp-center-icon" />
        </Transition>
      </div>

      <!-- Step label -->
      <Transition name="lp-step" mode="out-in">
        <p :key="currentStep" class="lp-step-label">{{ steps[currentStep] }}</p>
      </Transition>

      <!-- Step dots -->
      <div class="lp-dots">
        <span v-for="(_, idx) in steps" :key="idx" class="lp-dot"
              :class="{ done: idx < currentStep, active: idx === currentStep }" />
      </div>

      <!-- Progress bar -->
      <div class="lp-progress-row">
        <div class="lp-track">
          <div class="lp-fill" :style="{ width: progress + '%' }" />
        </div>
        <span class="lp-pct">{{ Math.round(progress) }}%</span>
      </div>

      <!-- Patience message -->
      <Transition name="lp-fade">
        <p v-if="patienceMessage" class="lp-patience">
          <i class="fa-solid fa-hourglass-half" />
          {{ patienceMessage }}
        </p>
      </Transition>

    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  message: string;
  progress: number;
  steps: string[];
  currentStep: number;
  showMinorTip?: boolean;
  patienceMessage?: string;
}>();

const stepConfig: Record<number, { icon: string }> = {
  0: { icon: 'fa-rocket' },
  1: { icon: 'fa-graduation-cap' },
  2: { icon: 'fa-list-check' },
  3: { icon: 'fa-puzzle-piece' },
  4: { icon: 'fa-map' },
  5: { icon: 'fa-star' },
};
</script>

<style scoped>
.lp-wrap {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 520px;
  padding: 1.25rem 1.5rem 3rem;
}

.lp-card {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.75rem;
}

/* ── Double-ring spinner ── */
.lp-spinner-wrap {
  position: relative;
  width: 140px;
  height: 140px;
  flex-shrink: 0;
}

.lp-svg {
  width: 140px;
  height: 140px;
}

.lp-center-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.9rem;
  color: #001e62;
}

/* ── Step label ── */
.lp-step-label {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
  margin: 0;
  line-height: 1.4;
}

/* ── Step dots ── */
.lp-dots {
  display: flex;
  gap: 0.45rem;
  align-items: center;
}

.lp-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e5e7eb;
  transition: all 0.35s ease;
}

.lp-dot.done {
  background: #fca5a5;
}

.lp-dot.active {
  background: #d50032;
  width: 22px;
  border-radius: 999px;
}

/* ── Progress bar ── */
.lp-progress-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  width: 100%;
}

.lp-track {
  flex: 1;
  height: 7px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.lp-fill {
  height: 100%;
  background: linear-gradient(90deg, #d50032, #001e62);
  border-radius: 999px;
  transition: width 0.7s ease;
}

.lp-pct {
  font-size: 0.9rem;
  font-weight: 700;
  color: #6b7280;
  min-width: 2.75rem;
  text-align: right;
}

/* ── Patience message ── */
.lp-patience {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  margin: 0;
  font-size: 0.9rem;
  color: #9ca3af;
  font-style: italic;
  line-height: 1.6;
  text-align: center;
}

.lp-patience .fa-solid {
  color: #fca5a5;
  font-style: normal;
}

/* ── Transitions ── */
.lp-step-enter-active,
.lp-icon-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.lp-step-leave-active,
.lp-icon-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.lp-step-enter-from,
.lp-icon-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.lp-step-leave-to,
.lp-icon-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.lp-fade-enter-active {
  transition: opacity 0.8s ease, transform 0.8s ease;
}
.lp-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
</style>
